(() => {
  const root = document.querySelector('.themerie-storefront');
  if (!root) return;

  // Mega-menu motion coordination (build-once, every brand). CSS does the
  // visuals via is-mega-* state; this sets that state and the timings.
  const megaNav = root.querySelector('.header__inline-menu');
  const megaPanels = megaNav ? Array.from(megaNav.querySelectorAll('details.mega-menu, details.th-menu-dropdown')) : [];
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const SWITCH_MS = 200; // outgoing stays underneath this long during a switch

  // Accordions (product tabs, FAQ, collapsible rows, footer columns on phones, gift note, discount
  // field): open and close by animating the <details> height, in every browser. CSS can only animate
  // to height:auto in Chromium (interpolate-size), so Safari on iPhone had no motion at all. A click
  // during an animation reverses it from where it is. Keyboard and screen readers get the same toggle.
  const ACCORDIONS =
    'details.faq-item, .product__accordion details, .accordion details, details.th-footer__fold, details.th-gift, details.cart__gift-note, details.cart-page__discount, details.disclosures__details';
  const ACCORDION_MS = 320;
  const ACCORDION_EASE = 'cubic-bezier(0.25, 0.8, 0.25, 1)';
  if (!reduceMotion && Element.prototype.animate) {
    document.addEventListener('click', (event) => {
      const summary = event.target.closest('summary');
      const details = summary && summary.parentElement;
      if (!details || details.tagName !== 'DETAILS' || summary !== details.firstElementChild) return;
      if (!details.matches(ACCORDIONS) || event.defaultPrevented) return;
      event.preventDefault();
      const opening = !details.open || details.classList.contains('is-accordion-closing');
      const from = details.getBoundingClientRect().height;
      if (details._accordionAnim) details._accordionAnim.cancel();
      details.open = false;
      const closed = details.getBoundingClientRect().height;
      details.open = true;
      const open = details.getBoundingClientRect().height;
      details.classList.toggle('is-accordion-closing', !opening);
      details.classList.add('is-accordion-moving');
      summary.setAttribute('aria-expanded', String(opening));
      const anim = details.animate(
        { height: [`${from}px`, `${opening ? open : closed}px`] },
        { duration: ACCORDION_MS, easing: ACCORDION_EASE }
      );
      details._accordionAnim = anim;
      anim.onfinish = () => {
        details._accordionAnim = null;
        details.classList.remove('is-accordion-moving');
        if (!opening) {
          details.open = false;
          details.classList.remove('is-accordion-closing');
        }
      };
    });
  }

  const CLOSE_MS = 170; // fade-out before the panel actually closes

  // Animated close: fade the panel out, THEN remove [open].
  const closePanel = (panel) => {
    if (!panel.open || panel.classList.contains('is-mega-closing')) return;
    panel.classList.remove('is-mega-switch', 'is-mega-leaving');
    panel.classList.add('is-mega-closing');
    window.clearTimeout(panel._closeAnimTimer);
    panel._closeAnimTimer = window.setTimeout(() => {
      panel.open = false;
      panel.classList.remove('is-mega-closing');
    }, CLOSE_MS);
  };

  // On open: mark a SWITCH (so CSS keeps the surface + shadow constant and only
  // swaps the content — no shadow pulse) and keep each already-open panel
  // underneath until the incoming has covered it, then close it. No empty frame.
  // A MutationObserver (microtask, before paint), not the async 'toggle' event: the incoming
  // panel never gets one frame painted without its switch state.
  megaPanels.forEach((details) => {
    new MutationObserver(() => {
      if (!details.open || details._megaWasOpen) {
        details._megaWasOpen = details.open;
        if (!details.open) details.classList.remove('is-mega-switch');
        return;
      }
      details._megaWasOpen = true;
      window.clearTimeout(details._closeAnimTimer);
      details.classList.remove('is-mega-closing', 'is-mega-leaving');
      const switching = megaPanels.some(
        (o) => o !== details && o.open && !o.classList.contains('is-mega-closing')
      );
      details.classList.toggle('is-mega-switch', switching);
      // Switch: one surface that morphs. The incoming panel lies on top from its first frame and
      // eases from the outgoing panel's height to its own; a copy of the outgoing content fades out
      // inside it while the new content fades in, so nothing snaps away and two layouts never stack.
      const content = details.querySelector('.mega-menu__content');
      const outgoing = switching && megaPanels.find(
        (o) => o !== details && o.open && !o.classList.contains('is-mega-closing')
      );
      const outgoingContent = outgoing && outgoing.querySelector('.mega-menu__content');
      if (content && outgoingContent && content.animate && !reduceMotion) {
        const EASE = 'cubic-bezier(0.65, 0, 0.35, 1)';
        const from = outgoingContent.offsetHeight;
        const to = content.offsetHeight;
        content.querySelectorAll(':scope > .th-mega-ghost').forEach((g) => g.remove());
        const ghost = document.createElement('div');
        ghost.className = 'th-mega-ghost';
        ghost.setAttribute('aria-hidden', 'true');
        ghost.inert = true;
        Array.from(outgoingContent.children).forEach((child) => {
          if (child.classList.contains('th-mega-ghost')) return;
          const copy = child.cloneNode(true);
          copy.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
          copy.removeAttribute('id');
          ghost.appendChild(copy);
        });
        content.appendChild(ghost);
        // direction of travel along the menu: content leaves that way, the new arrives from the other side
        const dir = megaPanels.indexOf(details) > megaPanels.indexOf(outgoing) ? 1 : -1;
        content.style.setProperty('--th-mega-dx', `${dir * 1.6}rem`);
        ghost
          .animate(
            [
              { opacity: 1, transform: 'none' },
              { opacity: 0, transform: `translateX(${dir * -1.6}rem)` },
            ],
            { duration: 200, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' }
          )
          .finished.then(() => ghost.remove(), () => ghost.remove());
        if (Math.abs(from - to) > 1) {
          content.animate(
            [
              { height: `${from}px`, overflow: 'hidden' },
              { height: `${to}px`, overflow: 'hidden' },
            ],
            { duration: 380, easing: EASE }
          );
        }
      }
      megaPanels.forEach((other) => {
        if (other === details || !other.open || other.classList.contains('is-mega-closing')) return;
        other.classList.remove('is-mega-switch');
        other.classList.add('is-mega-leaving');
        window.clearTimeout(other._megaCloseTimer);
        other._megaCloseTimer = window.setTimeout(() => {
          other.open = false;
          other.classList.remove('is-mega-leaving');
        }, SWITCH_MS);
      });
    }).observe(details, { attributes: true, attributeFilter: ['open'] });
  });

  // Hover-trigger mode (theme setting "mega_menu_trigger" = hover). Real hover
  // pointers only — touch and keyboard keep the native click/Enter toggle.
  // Intent delay on first open (menus you pass over don't pop); near-instant
  // once a panel is already open (you've committed to the menu).
  const hoverMode =
    megaNav &&
    megaNav.dataset.megaTrigger === 'hover' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  if (hoverMode && megaPanels.length) {
    const OPEN_FIRST = 200;
    const OPEN_SWITCH = 80;
    const LEAVE_DELAY = 240;
    let openTimer;
    let leaveTimer;
    const anyOpen = () => megaPanels.some((d) => d.open && !d.classList.contains('is-mega-closing'));

    megaPanels.forEach((details) => {
      const summary = details.querySelector('summary');
      details.addEventListener('mouseenter', () => {
        window.clearTimeout(leaveTimer);
        window.clearTimeout(openTimer);
        if (details.open) return;
        openTimer = window.setTimeout(
          () => {
            details.open = true;
          },
          anyOpen() ? OPEN_SWITCH : OPEN_FIRST
        );
      });
      details.addEventListener('mouseleave', () => window.clearTimeout(openTimer));
      // Mouse click shouldn't toggle (hover owns it); keyboard Enter (detail 0) still does.
      summary.addEventListener('click', (event) => {
        if (event.detail > 0) event.preventDefault();
      });
    });

    megaNav.addEventListener('mouseleave', () => {
      window.clearTimeout(openTimer);
      leaveTimer = window.setTimeout(() => megaPanels.forEach(closePanel), LEAVE_DELAY);
    });
    megaNav.addEventListener('mouseenter', () => window.clearTimeout(leaveTimer));
  } else {
    // Click mode: animate the close when a mouse click would toggle an open
    // panel shut (keyboard Enter keeps the instant native toggle).
    megaPanels.forEach((details) => {
      details.querySelector('summary').addEventListener('click', (event) => {
        if (details.open && event.detail > 0) {
          event.preventDefault();
          closePanel(details);
        }
      });
    });
  }

  // Mobile drawer — accordion mode. When the drawer's submenu style is "accordion", category
  // submenus expand in place instead of Dawn's slide-in panels. A capture-phase guard on the
  // drawer blocks Dawn's per-summary click handler (slide + focus-trap) for submenu summaries;
  // the native <details> toggle still fires (we don't preventDefault). We own aria-expanded.
  const headerDrawer = root.querySelector('header-drawer');
  if (headerDrawer && headerDrawer.dataset.submenuStyle === 'accordion') {
    const drawerMainToggle = headerDrawer.querySelector('details');
    headerDrawer.addEventListener(
      'click',
      (event) => {
        const summary = event.target.closest('summary');
        if (!summary) return;
        const details = summary.parentElement;
        if (!details || details.tagName !== 'DETAILS' || details === drawerMainToggle) return;
        // Submenu summary: stop the click reaching Dawn's slide handler; native toggle still runs.
        event.stopPropagation();
      },
      true
    );
    headerDrawer.querySelectorAll('details').forEach((details) => {
      if (details === drawerMainToggle) return;
      const summary = details.querySelector('summary');
      if (!summary) return;
      summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      details.addEventListener('toggle', () => {
        summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      });
    });
  }

  // Tile-wall look: the custom Tilerie sections join Dawn's reveal on scroll (animations.js, which
  // runs after this file), so they slide in like the product cards. Grids cascade item by item.
  // The hero and page heroes stay put: they're the first paint (and the LCP).
  if (
    document.body.classList.contains('th-look--grout') &&
    document.querySelector('script[src*="animations.js"]') &&
    !(window.Shopify && window.Shopify.designMode)
  ) {
    const tag = (el, cascade) => {
      if (!el || el.classList.contains('scroll-trigger')) return;
      el.classList.add('scroll-trigger', 'animate--slide-in');
      if (cascade) el.setAttribute('data-cascade', '');
    };
    document.querySelectorAll('section.tg:not(.tg-hero):not(.tg-phero):not(.tg-pw) > .tg-wrap, figure.tg-photo').forEach((wrap) => {
      if (wrap.matches('figure')) return tag(wrap);
      Array.from(wrap.children).forEach((child) => {
        const items = Array.from(child.children).filter((item) => item.tagName !== 'TEMPLATE');
        const display = getComputedStyle(child).display;
        const isGrid = (display === 'grid' || display === 'flex') && items.length > 1 && items.length <= 12;
        if (isGrid) items.forEach((item) => tag(item, true));
        else tag(child);
      });
    });
  }

  const revealItems = Array.from(document.querySelectorAll('[data-memerie-reveal]'));

  if (!revealItems.length || reduceMotion || !('IntersectionObserver' in window)) {
    revealItems.forEach((item) => item.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -12% 0px', threshold: 0.12 }
  );

  revealItems.forEach((item) => observer.observe(item));
})();
